import React from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { TrafficLight, PedestrianLight } from '../regulator/TrafficLight';
import { colorCodeToLightStates } from '../../utils/colorCodeMapper';
import { useMapBearing, useMapZoom } from './MapBearingContext';
import type { GroupDefinition, ColorCode } from '../../types/intersection';
import { ColorCode as CC } from '../../types/intersection';

// Dominant color for each ColorCode (used for the low-zoom dot)
const CODE_DOT_COLOR: Record<ColorCode, string> = {
  [CC.D]: '#6b7280', [CC.N]: '#374151',
  [CC.V]: '#22c55e', [CC.P]: '#22c55e',
  [CC.A]: '#f59e0b', [CC.C]: '#f59e0b', [CC.E]: '#f59e0b', [CC.Z]: '#f59e0b',
  [CC.R]: '#ef4444', [CC.H]: '#ef4444', [CC.F]: '#ef4444',
  [CC.S]: '#22c55e', [CC.I]: '#22c55e', [CC.G]: '#22c55e',
  [CC.B]: '#ef4444', [CC.J]: '#ef4444', [CC.K]: '#ef4444',
};

interface TrafficLightMarkerProps {
  group: GroupDefinition;
  colorCode: ColorCode;
  offset?: [number, number]; // [lng, lat] offset for co-located poles
  // Posición real del semáforo OSM (Modo Edición → cableado de grupos).
  // Presente = el grupo converge a una posición verificada en campo, en vez
  // de la posición sintética generada (reguladores-scg.ts).
  realPosition?: [number, number];
}

export const TrafficLightMarker = React.memo(function TrafficLightMarker({
  group,
  colorCode,
  offset,
  realPosition,
}: TrafficLightMarkerProps) {
  const mapBearing = useMapBearing();
  const mapZoom = useMapZoom();
  const lights = colorCodeToLightStates(colorCode);
  const isPedestrian = group.type === 'P';
  const typeLabel = group.type === 'V' ? 'Semáforo vehicular'
    : group.type === 'P' ? 'Semáforo peatonal'
    : 'Semáforo bus';
  const isWired = realPosition != null;

  const lng = isWired ? realPosition[0] : group.position[0] + (offset?.[0] ?? 0);
  const lat = isWired ? realPosition[1] : group.position[1] + (offset?.[1] ?? 0);

  const showDetailed = mapZoom >= 17;

  // Low-zoom: colored dot (same pattern as TrafficSignalDot)
  if (!showDetailed) {
    const size = Math.min(14, Math.max(5, (mapZoom - 13) * 2.5 + 5));
    const dotColor = CODE_DOT_COLOR[colorCode] ?? '#6b7280';

    return (
      <Marker longitude={lng} latitude={lat} anchor="center" style={{ zIndex: 2 }}>
        <div className="relative group/marker">
          <div
            className="rounded-full transition-colors duration-300"
            style={isWired ? {
              width: size,
              height: size,
              backgroundColor: dotColor,
              border: `1.5px solid ${dotColor}88`,
              boxShadow: `0 0 6px ${dotColor}66`,
            } : {
              width: size,
              height: size,
              backgroundColor: `${dotColor}33`,
              border: `1.5px dashed ${dotColor}aa`,
            }}
          />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap pointer-events-none opacity-0 group-hover/marker:opacity-100 transition-opacity z-40">
            <span className="text-[10px] font-mono bg-gray-900/90 text-gray-300 px-1.5 py-0.5 rounded shadow">
              G{group.id} · {typeLabel}{!isWired && ' · posición provisional'}
            </span>
          </div>
        </div>
      </Marker>
    );
  }

  // High-zoom: full 3D traffic light
  const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));

  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="bottom"
      style={{ zIndex: 2 }}
    >
      <div
        className={`flex flex-col items-center group/marker cursor-pointer ${isWired ? '' : 'opacity-70'}`}
        style={{ transform: `scale(${scale})` }}
        title={group.label}
      >
        {/* Semáforo — flota mientras la posición es provisional; se asienta al cablear a la posición real */}
        <div
          className={isWired ? undefined : 'animate-float'}
          style={{ transform: `rotate(${group.bearing - mapBearing}deg)` }}
        >
          {isPedestrian ? (
            <PedestrianLight
              red={lights.red}
              green={lights.green}
              size="sm"
            />
          ) : (
            <TrafficLight
              red={lights.red}
              amber={lights.amber}
              green={lights.green}
              size="sm"
            />
          )}
        </div>
        {/* Poste */}
        <div className="w-[2px] h-3 bg-gray-500/70" />
        {/* Sombra en el suelo */}
        <div className="w-3 h-1 rounded-full bg-black/25 blur-[1px]" />
      </div>
      {/* Tooltip */}
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none opacity-0 group-hover/marker:opacity-100 transition-opacity">
        <span className="text-[10px] font-mono bg-gray-900/90 text-gray-300 px-1.5 py-0.5 rounded">
          G{group.id} · {typeLabel}{!isWired && ' · provisional'}
        </span>
      </div>
    </Marker>
  );
});
